import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentPeriod, fmtDate, inr, P, periodLabel } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { useMut } from '../../../lib/query';
import { useGo } from '../../../navigation/nav';
import { Alert, Badge, Button, DataTable, Modal, PageHeader, Screen, StatCard, StatusBadge, Text } from '../../../ui';
import { AutoGrid, PeriodSelect } from '../components/common';

export function PayrollRunsScreen() {
  const { can } = useAuth();
  const go = useGo();
  const manage = can(P.PAYROLL_MANAGE);
  const q = useQuery({ queryKey: ['payroll', 'runs'], queryFn: () => api.payroll.runs() });
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(currentPeriod());
  const create = useMut(() => api.payroll.createRun(period), {
    invalidate: [['payroll', 'runs']],
    success: 'Payroll run created',
    onSuccess: (r) => {
      setOpen(false);
      go('PayrollRun', { id: r.id });
    },
  });
  const runs = [...(q.data ?? [])].sort((a, b) => b.period.localeCompare(a.period));
  const exists = runs.some((r) => r.period === period);
  const latest = runs[0];
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="payroll-runs">
      <PageHeader
        title="Payroll runs"
        subtitle="Create a run per month, process, verify, lock and mark as paid."
        actions={
          <>
            {manage && <Button variant="primary" icon="play" title="New payroll run" onPress={() => setOpen(true)} testID="new-run" />}
            {manage && <Button icon="grid" title="Inputs" onPress={() => go('PayrollInputs')} />}
            <Button icon="download" title="Bank file & challans" onPress={() => go('PayrollOutputs')} />
          </>
        }
      />
      {latest && (
        <AutoGrid min={170}>
          <StatCard label={`Latest · ${periodLabel(latest.period)}`} value={<StatusBadge status={latest.status} />} icon="play" onPress={() => go('PayrollRun', { id: latest.id })} />
          <StatCard label="Employees" value={latest.employee_count} icon="users" tone="neutral" />
          <StatCard label="Net pay" value={inr(latest.total_net)} icon="wallet" tone="success" />
          <StatCard label="Employer cost" value={inr(latest.total_employer_cost)} icon="building" tone="info" />
        </AutoGrid>
      )}
      <DataTable
        rows={runs}
        loading={q.isLoading}
        onRowPress={(r) => go('PayrollRun', { id: r.id })}
        emptyTitle="No payroll runs yet"
        emptyDescription={manage ? 'Create a run for the month once attendance is locked and inputs are final.' : undefined}
        testID="runs-table"
        columns={[
          { key: 'period', title: 'Period', width: 150, render: (r) => <Text weight="semibold">{periodLabel(r.period)}</Text>, sortValue: (r) => r.period },
          { key: 'status', title: 'Status', width: 110, render: (r) => <StatusBadge status={r.status} />, sortValue: (r) => r.status },
          { key: 'employee_count', title: 'Employees', width: 100, align: 'right' },
          { key: 'total_gross', title: 'Gross', width: 130, align: 'right', render: (r) => inr(r.total_gross), sortValue: (r) => r.total_gross },
          { key: 'total_deductions', title: 'Deductions', width: 120, align: 'right', render: (r) => inr(r.total_deductions), sortValue: (r) => r.total_deductions },
          { key: 'total_net', title: 'Net pay', width: 130, align: 'right', render: (r) => inr(r.total_net), sortValue: (r) => r.total_net },
          { key: 'total_employer_cost', title: 'Employer cost', width: 130, align: 'right', render: (r) => inr(r.total_employer_cost), sortValue: (r) => r.total_employer_cost },
          { key: 'variance_flags', title: 'Variance', width: 90, align: 'right', render: (r) => (r.variance_flags ? <View><Badge tone="warning">{r.variance_flags}</Badge></View> : '—'), sortValue: (r) => r.variance_flags },
          { key: 'paid_at', title: 'Paid', width: 110, render: (r) => fmtDate(r.paid_at), sortValue: (r) => r.paid_at },
        ]}
      />
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New payroll run"
        width={420}
        footer={
          <>
            <Button title="Cancel" onPress={() => setOpen(false)} />
            <Button variant="primary" title="Create run" disabled={exists} loading={create.isPending} onPress={() => create.mutate(undefined)} testID="create-run" />
          </>
        }
      >
        <PeriodSelect label="Payroll period" value={period} onChange={setPeriod} />
        {exists ? <Alert tone="warning">A run already exists for {periodLabel(period)}.</Alert> : <Text variant="muted">Make sure attendance for the month is locked and payroll inputs are final.</Text>}
      </Modal>
    </Screen>
  );
}
